import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  Document,
  TranscriptorEncounterResponse,
} from '@/shared/api/transcriptor/transcriptorApi';
import { SliceName } from '@/shared/config';

type EncounterState = {
  byId: Record<string, TranscriptorEncounterResponse>;
};

const initialState: EncounterState = {
  byId: {},
};

const patchDocumentApprovedInEncounter = (
  encounter: TranscriptorEncounterResponse,
  documentId: string,
  approved: boolean,
): TranscriptorEncounterResponse => {
  const patchDoc = (doc: Document | undefined) =>
    doc && doc.id === documentId ? { ...doc, approved } : doc;

  return {
    ...encounter,
    documents: encounter.documents?.map((doc) =>
      doc.id === documentId ? { ...doc, approved } : doc,
    ),
    summary: patchDoc(encounter.summary),
    evaluation: patchDoc(encounter.evaluation),
    follow_up: patchDoc(encounter.follow_up),
    literature_review: patchDoc(encounter.literature_review),
  };
};

// Detail-only fields — present in GET /encounters/{id}, absent (or empty)
// in GET /encounters previews. mergePreviewMany preserves whichever of these
// were populated by a prior detail fetch, so a list refetch racing a detail
// fetch on hard-refresh cannot blank the encounter's sections.
const detailFieldsOf = (encounter: TranscriptorEncounterResponse) => ({
  fragments: encounter.fragments,
  documents: encounter.documents,
  summary: encounter.summary,
  evaluation: encounter.evaluation,
  follow_up: encounter.follow_up,
  literature_review: encounter.literature_review,
  analytics: encounter.analytics,
});

const encounterSlice = createSlice({
  name: SliceName.encounter,
  initialState,
  reducers: {
    upsertDetail: (
      state,
      action: PayloadAction<TranscriptorEncounterResponse>,
    ) => {
      state.byId[action.payload.id] = action.payload;
    },
    mergePreviewMany: (
      state,
      action: PayloadAction<TranscriptorEncounterResponse[]>,
    ) => {
      action.payload.forEach((preview) => {
        const existing = state.byId[preview.id];

        if (!existing) {
          state.byId[preview.id] = preview;
          return;
        }

        state.byId[preview.id] = {
          ...preview,
          ...detailFieldsOf(existing),
        };
      });
    },
    patchDocumentApproved: (
      state,
      action: PayloadAction<{
        encounterId: string;
        documentId: string;
        approved: boolean;
      }>,
    ) => {
      const { encounterId, documentId, approved } = action.payload;
      const encounter = state.byId[encounterId];

      if (!encounter) {
        return;
      }

      state.byId[encounterId] = patchDocumentApprovedInEncounter(
        encounter,
        documentId,
        approved,
      );
    },
    removeOne: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
    },
  },
});

export const { actions } = encounterSlice;

export default encounterSlice.reducer;
